import React from "react";
import {
  Architecture,
  ArchitectureOutlined,
  Checklist,
  ChecklistOutlined,
  ConfirmationNumber,
  ConfirmationNumberOutlined,
  Group,
  GroupOutlined,
  Groups,
  GroupsOutlined,
  Handshake,
  HandshakeOutlined,
  Help,
  HelpOutlineOutlined,
  LogoutOutlined,
  Menu as MenuIcon,
  Psychology,
  PsychologyOutlined,
  ShoppingCart,
  ChevronLeft,
  ChevronRight,
} from "@mui/icons-material";
//import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
//import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import { Badge, Box, Drawer, IconButton, Fade } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import {
  getUserRolesFromToken,
  RolesEnum,
} from "../../utils/getUserRolesFromToken";
import { logout } from "../LoginGuard/loginGuard";
import PreservingLink from "../PreservingLink";
import {
  HeaderContainer,
  HeaderContent,
  HeaderLogoutWrapper,
  SidebarContainer,
  SidebarItem,
  SidebarList,
  SidebarLogo,
  SidebarLogoWrapper,
  SidebarLabel,
} from "./styles";
import { CartSingleton } from "../CartSingleton";

type SidebarIconComponent = typeof Group;

interface NavigationItem {
  to: string;
  label: string;
  icon: SidebarIconComponent;
  activeIcon?: SidebarIconComponent;
  isVisible?: boolean;
}

interface HeaderProps {
  onClickOptions?: () => void;
}

const DRAWER_WIDTH = 260;
const MINI_DRAWER_WIDTH = 88;
const MINI_DRAWER_STORAGE_KEY = "sidebar-mini-drawer-open";

export function Header({ onClickOptions }: HeaderProps) {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const [cartItemsCount, setCartItemsCount] = React.useState<number>(() => {
    const snapshot = CartSingleton.shared.getCartSnapshot();
    if (!snapshot) {
      return 0;
    }
    return snapshot.items.reduce((acc, cur) => acc + cur.quantity, 0);
  });

  React.useEffect(() => {
    const updateCartCount = () => {
      const snapshot = CartSingleton.shared.getCartSnapshot();
      const count = snapshot
        ? snapshot.items.reduce((acc, cur) => acc + cur.quantity, 0)
        : 0;
      setCartItemsCount(count);
    };

    CartSingleton.shared.addCartUpdatedListener(updateCartCount);
    void CartSingleton.shared.ensureLoaded()
      .then(updateCartCount)
      .catch(() => updateCartCount());

    return () => {
      CartSingleton.shared.removeCartUpdatedListener(updateCartCount);
    };
  }, []);

  const pathname = window.location.pathname;

  return (
    <HeaderContainer>
      <HeaderContent>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
          }}
        >
          {!isDesktop && onClickOptions && (
            <IconButton
              aria-label="Abrir menu de navegação"
              onClick={onClickOptions}
              sx={{ color: "white" }}
            >
              <MenuIcon />
            </IconButton>
          )}
        </Box>

        <HeaderLogoutWrapper>
          <Box
            component={PreservingLink}
            to="/checkout/review"
            sx={{
              display: "flex",
              flexDirection: "column",
              margin: "0px",
            }}
          >
            <SidebarItem
              selected={pathname === "/checkout/review"}
              onlyIcon
              defaultColor={theme.palette.common.white}
            >
              <Badge badgeContent={cartItemsCount} color="error">
                <ShoppingCart sx={{ fontSize: 32 }} />
              </Badge>
            </SidebarItem>
          </Box>
        </HeaderLogoutWrapper>
      </HeaderContent>
    </HeaderContainer>
  );
}

interface SidebarProps {
  showMenu?: boolean;
  onClose?: () => void;
}

export function Sidebar({ showMenu, onClose }: SidebarProps) {
  const theme = useTheme();
  const pathname = window.location.pathname;
  const userRoles = getUserRolesFromToken();
  const isAdmin = userRoles.includes(RolesEnum.ADMIN);
  const isReseller = userRoles.includes(RolesEnum.RESELLER);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [isSidebarHovered, setIsSidebarHovered] = React.useState(false);
  const [isDesktopDrawerOpen, setIsDesktopDrawerOpen] = React.useState(() => {
    if (typeof window === "undefined") {
      return true;
    }
    try {
      const storedValue = window.localStorage.getItem(
        MINI_DRAWER_STORAGE_KEY
      );
      if (storedValue === null) {
        return true;
      }
      return storedValue === "true";
    } catch {
      return true;
    }
  });
  const shouldShowLabels = isDesktop ? isDesktopDrawerOpen : !!showMenu;
  const isDesktopMiniDrawer = isDesktop && !isDesktopDrawerOpen;
  const menuBackgroundColor = theme.palette.primary.main;
  const drawerWidth = isDesktop
    ? (isDesktopDrawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH)
    : DRAWER_WIDTH;

  const handleDrawerClose = React.useCallback(() => {
    if (onClose) {
      onClose();
    }
  }, [onClose]);

  const closeIfMobile = React.useCallback(() => {
    if (!isDesktop) {
      handleDrawerClose();
    }
  }, [handleDrawerClose, isDesktop]);

  const handleLogout = React.useCallback(() => {
    logout();
    closeIfMobile();
  }, [closeIfMobile]);
  const handleToggleDesktopDrawer = React.useCallback(() => {
    if (isDesktop) {
      setIsDesktopDrawerOpen((prev) => !prev);
    }
  }, [isDesktop]);

  const readStoredDesktopDrawerPreference = React.useCallback((): boolean => {
    if (typeof window === "undefined") {
      return true;
    }
    try {
      const storedValue = window.localStorage.getItem(
        MINI_DRAWER_STORAGE_KEY
      );
      if (storedValue === null) {
        return true;
      }
      return storedValue === "true";
    } catch {
      return true;
    }
  }, []);

  React.useEffect(() => {
    if (!isDesktop) {
      setIsDesktopDrawerOpen(true);
      return;
    }
    const storedPreference = readStoredDesktopDrawerPreference();
    setIsDesktopDrawerOpen(storedPreference);
  }, [isDesktop, readStoredDesktopDrawerPreference]);

  React.useEffect(() => {
    if (!isDesktopMiniDrawer) {
      setIsSidebarHovered(false);
    }
  }, [isDesktopMiniDrawer]);

  React.useEffect(() => {
    if (!isDesktop || typeof window === "undefined") {
      return;
    }
    try {
      window.localStorage.setItem(
        MINI_DRAWER_STORAGE_KEY,
        isDesktopDrawerOpen ? "true" : "false"
      );
    } catch {
      // ignore write errors
    }
  }, [isDesktop, isDesktopDrawerOpen]);

  const handleSidebarMouseEnter = React.useCallback(() => {
    if (isDesktopMiniDrawer) {
      setIsSidebarHovered(true);
    }
  }, [isDesktopMiniDrawer]);

  const handleSidebarMouseLeave = React.useCallback(() => {
    if (isDesktopMiniDrawer) {
      setIsSidebarHovered(false);
    }
  }, [isDesktopMiniDrawer]);

  const showMiniExpandButton = isDesktopMiniDrawer && isSidebarHovered;

  const mainMenuItems: NavigationItem[] = [
    // {
    //   to: "/",
    //   label: "Explorar",
    //   icon: SpaceDashboardOutlinedIcon,
    //   activeIcon: SpaceDashboardIcon,
    // },
    {
      to: "/instruments",
      label: "Instrumentos",
      icon: ArchitectureOutlined,
      activeIcon: Architecture,
    },
    {
      to: "/assignments",
      label: "Aplicações",
      icon: ChecklistOutlined,
      activeIcon: Checklist,
      isVisible: !isReseller || isAdmin,
    },
    {
      to: "/users",
      label: "Respondentes e devolutivas",
      icon: GroupOutlined,
      activeIcon: Group,
      isVisible: !isReseller || isAdmin,
    },
    {
      to: "/groups",
      label: "Devolutivas agregadas",
      icon: GroupsOutlined,
      activeIcon: Groups,
      isVisible: !isReseller || isAdmin,
    },
    {
      to: "/coupons",
      label: "Cupons",
      icon: ConfirmationNumberOutlined,
      activeIcon: ConfirmationNumber,
      isVisible: isAdmin,
    },
    {
      to: "/psichologists",
      label: "Psicólogos",
      icon: PsychologyOutlined,
      activeIcon: Psychology,
      isVisible: isAdmin,
    },
    {
      to: "/reseller/management",
      label: "Gestão de clientes",
      icon: HandshakeOutlined,
      activeIcon: Handshake,
      isVisible: isReseller,
    },
  ].filter(({ isVisible }) => isVisible ?? true);

  const secondaryMenuItems: NavigationItem[] = [
    {
      to: "/tutorials",
      label: "Tutoriais",
      icon: HelpOutlineOutlined,
      activeIcon: Help,
    },
  ];

  const menuContent = (
    <SidebarContainer
      id="header-menu"
      onMouseEnter={handleSidebarMouseEnter}
      onMouseLeave={handleSidebarMouseLeave}
    >
      <SidebarLogoWrapper
        style={{
          justifyContent: isDesktopMiniDrawer ? "center" : "space-between",
        }}
      >
        <Box
          sx={{
            position: "relative",
            minWidth: isDesktopMiniDrawer ? theme.spacing(5) : theme.spacing(15),
            minHeight: theme.spacing(5),
          }}
        >
          <Fade
            in={!isDesktopMiniDrawer}
            timeout={200}
            mountOnEnter
            unmountOnExit
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
              }}
            >
              <SidebarLogo
                src="/images/logos/logo.png"
                alt="Logo catvante"
              />
            </Box>
          </Fade>
          <Fade
            in={isDesktopMiniDrawer && !showMiniExpandButton}
            timeout={200}
            mountOnEnter
            unmountOnExit
          >
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SidebarLogo
                src="/images/logos/logo-mini.png"
                alt="Logo catvante"
                mini
              />
            </Box>
          </Fade>
          {isDesktop && showMiniExpandButton && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <IconButton
                aria-label="Expandir menu"
                onClick={handleToggleDesktopDrawer}
                size="medium"
                sx={{ color: theme.palette.common.white }}
              >
                <ChevronRight />
              </IconButton>
            </Box>
          )}
        </Box>
        {isDesktop && !isDesktopMiniDrawer && (
          <IconButton
            aria-label="Recolher menu"
            onClick={handleToggleDesktopDrawer}
            size="small"
            sx={{ color: theme.palette.common.white }}
          >
            <ChevronLeft />
          </IconButton>
        )}
      </SidebarLogoWrapper>

      <SidebarList>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          {mainMenuItems.map(
            ({ to, label, icon: InactiveIcon, activeIcon: ActiveIcon }) => {
              const selected = pathname === to;
              const IconComponent =
                selected && ActiveIcon ? ActiveIcon : InactiveIcon;

              return (
                <Box
                  key={to}
                  component={PreservingLink}
                  to={to}
                  onClick={closeIfMobile}
                >
                  <SidebarItem
                    selected={selected}
                    showMenu={shouldShowLabels}
                  >
                    <IconComponent />
                    <SidebarLabel selected={selected} visible={shouldShowLabels}>
                      {label}
                    </SidebarLabel>
                  </SidebarItem>
                </Box>
              );
            }
          )}
        </Box>


        <Box display={'flex'} flexDirection={'column'} gap={1}>
          {secondaryMenuItems.map(
            ({ to, label, icon: InactiveIcon, activeIcon: ActiveIcon }) => {
              const selected = pathname === to;
              const IconComponent =
                selected && ActiveIcon ? ActiveIcon : InactiveIcon;

              return (
                <Box
                  key={to}
                  component={PreservingLink}
                  to={to}
                  onClick={closeIfMobile}
                >
                  <SidebarItem
                    selected={selected}
                    showMenu={shouldShowLabels}
                  >
                    <IconComponent />
                    <SidebarLabel selected={selected} visible={shouldShowLabels}>
                      {label}
                    </SidebarLabel>
                  </SidebarItem>
                </Box>
              );
            }
          )}
          <SidebarItem onClick={handleLogout} showMenu={shouldShowLabels}>
            <LogoutOutlined />
            <SidebarLabel visible={shouldShowLabels}>
              Sair da conta
            </SidebarLabel>
          </SidebarItem>
        </Box>
      </SidebarList>
    </SidebarContainer>
  );

  return (
    <Drawer
      anchor="left"
      variant={isDesktop ? "permanent" : "temporary"}
      open={isDesktop ? true : !!showMenu}
      onClose={handleDrawerClose}
      ModalProps={{ keepMounted: true }}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        whiteSpace: "nowrap",
        transition: theme.transitions.create("width", {
          duration: theme.transitions.duration.standard,
        }),
        "& .MuiDrawer-paper": {
          width: drawerWidth,
          boxSizing: "border-box",
          backgroundColor: menuBackgroundColor,
          borderRight: "none",
          overflowX: "hidden",
        },
        boxShadow: '2px 0 15px rgba(100, 100, 100, 0.05)',
        zIndex: theme.zIndex.drawer,
      }}
    >
      {menuContent}
    </Drawer>
  );
}
